import React from "react";
import { ChakraProvider } from "@chakra-ui/react";
import { useOverlay } from "@toss/use-overlay";

import { customEvent } from "../../../utils";
import { chakraTheme } from "../../../styles";
import { OverlayModal } from "../../Modal/OverlayModal";
import { EventDomainType, EventPathType } from "../../../types";
import {
  CREMATORIUM_INQUIRY_OPTIONS,
  CrematoriumInquiryModal,
  CrematoriumInquiryModalHeader,
  InquiryOption,
  PARLOR_INQUIRY_OPTIONS,
} from "./CrematoriumInquiryModal";

/**
 * 화장장 시설 상세의 문의 유형 선택 모달을 연다.
 *
 * 모달을 여는 버튼(페이지 CTA, 하단 고정바 StepGuideFooter)은 기존 이벤트를
 * 그대로 쏘고, 새 이벤트는 모달 안에서만 발화한다.
 * 바깥 이벤트 이름을 건드리면 기존 시계열이 끊긴다.
 */
export const useInquiryModal = ({
  eventDomain,
  eventPath,
  options,
}: {
  eventDomain: keyof typeof EventDomainType;
  eventPath: EventPathType;
  options: InquiryOption[];
}) => {
  const overlay = useOverlay();

  /** @param facilityPhone 화장장 대표번호. 없으면 null 을 넘기면 고이로 폴백한다. */
  const openInquiryModal = (facilityPhone: string | null) => {
    overlay.open(({ isOpen, close }) => {
      /** × 와 오버레이 클릭 양쪽이 같은 경로를 타도록 여기서 이벤트를 쏜다. */
      const closeWithEvent = () => {
        customEvent({
          domain: eventDomain,
          path: eventPath,
          trigger_and_target: "ask-close",
        });
        close();
      };

      /**
       * 포털 앱 루트는 MUI 전용이라 ChakraProvider 가 없다(metro 제외 5곳).
       * OverlayModal 이 Chakra 기반이어서 여기서 직접 두른다.
       * FileRequestModal, BinsoReserveModal, SearchRequestModal 과 같은 방식이다.
       */
      return (
        <ChakraProvider theme={chakraTheme}>
          <OverlayModal
            isOpen={isOpen}
            onClose={closeWithEvent}
            closeOnOverlayClick
            // 좌우 여백은 이 레포 모달 컨벤션(FileRequestModal 등)과 같게 맞춘다
            modalContentStyle={{
              maxWidth: "400px",
              mx: "20px",
              borderRadius: "16px",
            }}
            header={{
              content: (
                <CrematoriumInquiryModalHeader onClose={closeWithEvent} />
              ),
            }}
            overlayModalContent={
              <CrematoriumInquiryModal
                eventDomain={eventDomain}
                eventPath={eventPath}
                options={options}
                facilityPhone={facilityPhone}
                onClose={close}
              />
            }
          />
        </ChakraProvider>
      );
    });
  };

  return { openInquiryModal };
};

/** 화장장 상세용 프리셋 */
export const useCrematoriumInquiryModal = (args: {
  eventDomain: keyof typeof EventDomainType;
  eventPath: EventPathType;
}) => useInquiryModal({ ...args, options: CREMATORIUM_INQUIRY_OPTIONS });

/** 장례식장 상세용 프리셋. 조문 문의만 장례식장 직통으로 간다. */
export const useParlorInquiryModal = (args: {
  eventDomain: keyof typeof EventDomainType;
  eventPath: EventPathType;
}) => useInquiryModal({ ...args, options: PARLOR_INQUIRY_OPTIONS });
